import { Box, Button, Grid, Tab, Tabs, Typography } from '@mui/material';
import React from 'react';
import { connect } from 'react-redux';
import { NavLink as RouterLink } from 'react-router-dom';
import { Observable, firstValueFrom, tap } from 'rxjs';

import { If } from 'app/components/if';
import { RequireRole } from 'app/components/require-role';
import { TabPanel } from 'app/components/tab-panel';
import { Role } from 'app/constants/role';
import { Brand } from 'app/model/cris/brand';
import { BrandAttributes } from 'app/model/cris/brand-attributes';
import { attributeService } from 'app/services/brand/attribute.service';
import { brandService } from 'app/services/brand.service';
import { dialogService } from 'app/services/dialog.service';
import { messagingService } from 'app/services/messaging.service';
import { RootState } from 'app/store';
import { ModelWithoutId } from 'app/type';
import { BrandDetailView } from 'features/cris/brands/brand-detail-view';
import { AttributeDialog } from 'features/cris/stores/attribute-dialog';

import { AttributeGrid } from './attribute-grid';
import { BrandAttributeHistory } from './brand-attribute-history';
import { BrandDetailsMoveTaxonomyTab } from './brand-details-move-taxonomy-tab';
import {
    prepareAttributes,
    saveNewAttribute,
    selectPossibleAttributes,
    selectProdDataStatus,
    setCurrentBrand,
} from '../cris.slice';
import { StoreListGrid } from '../stores/store-list-grid';

const TAB_STORES = 0;
const TAB_ATTRIBUTES = 1;
const TAB_ATTRIBUTE_HISTORY = 2;
const TAB_MOVE_TAXONOMY = 3;

interface BrandDetailsProps {
    brand: Brand;

    // redux props
    possibleAttributes?: string[];
    showProd?: boolean;

    // redux actions
    setCurrentBrand: (brand: Brand) => void;

    prepareAttributes: () => void;

    saveNewAttribute: (attr: ModelWithoutId<BrandAttributes>, brand: Brand) => Observable<BrandAttributes>;
}

interface BrandDetailsState {
    tab: number;
    addForm: boolean;
}

class BrandDetails extends React.Component<BrandDetailsProps, BrandDetailsState> {
    public constructor(props: BrandDetailsProps | Readonly<BrandDetailsProps>) {
        super(props);

        this.state = {
            tab: 0,
            addForm: false,
        };
    }

    public componentDidMount(): void {
        this.props.prepareAttributes();
    }

    public deployToProd(): void {
        dialogService.showConfirm(
            {
                message: 'Are you sure you want to deploy to production?',
                confirmText: 'Deploy',
            },
            () =>
                brandService.deploy(this.props.brand).subscribe(() => {
                    messagingService.showSuccess('Deployed');
                }),
        );
    }

    public render(): React.ReactNode {
        return (
            <Grid container spacing={2}>
                <Grid item xs={4}>
                    <BrandDetailView brand={this.props.brand} />
                    <RequireRole roles={Role.TAXONOMY_ADMIN}>
                        <If condition={!this.props.showProd}>
                            <Typography className='mt-2'>
                                Deploying will push Brand and all its attributes to production
                            </Typography>
                            <Button
                                type='button'
                                variant='outlined'
                                color='secondary'
                                className='mt-1 mr-1'
                                component={RouterLink}
                                to={`/cris/brands/${this.props.brand.id}`}
                            >
                                Edit
                            </Button>
                            <Button
                                type='button'
                                variant='outlined'
                                color='error'
                                className='mt-1 mr-1'
                                component={RouterLink}
                                to={`/cris/brands/${this.props.brand.id}/delete`}
                            >
                                Delete
                            </Button>
                            <Button
                                type='button'
                                variant='contained'
                                color='success'
                                className='mt-1'
                                onClick={() => this.deployToProd()}
                            >
                                Deploy Prod
                            </Button>
                        </If>
                    </RequireRole>
                </Grid>
                <Grid item xs={8}>
                    <Tabs
                        value={this.state.tab}
                        onChange={(evt, tab) => {
                            this.doTabSwitch(tab as number);
                        }}
                        indicatorColor='primary'
                        textColor='primary'
                        centered
                    >
                        <Tab label='Stores' />
                        <Tab label='Brand Attributes' />
                        <Tab label='Attribute History' />
                        <RequireRole roles={[Role.TAXONOMY_ADMIN]}>
                            <Tab label='Move Taxonomy' />
                        </RequireRole>
                    </Tabs>
                    <TabPanel value={this.state.tab} index={TAB_STORES}>
                        <StoreListGrid
                            rows={this.props.brand.stores ?? []}
                            paginationModel={{ pageSize: 25, page: 0 }}
                            disableRowSelectionOnClick
                            autoHeight
                            density='compact'
                        />
                    </TabPanel>
                    <TabPanel value={this.state.tab} index={TAB_ATTRIBUTES}>
                        <Box display='flex'>
                            <If condition={!this.props.showProd}>
                                <Typography variant='body1'>
                                    Edit value/details via table (double click or focus and Enter)
                                </Typography>
                                <Box flexGrow={1} />
                                <Button
                                    variant='outlined'
                                    className='pull-right mb-1'
                                    onClick={() => this.setState({ addForm: true })}
                                >
                                    Add Attribute
                                </Button>
                            </If>
                        </Box>
                        <AttributeDialog
                            open={this.state.addForm}
                            onClose={() => this.setState({ addForm: false })}
                            onAddAttribute={(attr: ModelWithoutId<BrandAttributes>) => {
                                this.props
                                    .saveNewAttribute(attr, this.props.brand)
                                    .subscribe(() => this.setState({ addForm: false }));
                            }}
                            type='brand'
                        />
                        <AttributeGrid
                            rows={this.props.brand.attributes ?? []}
                            paginationModel={{ pageSize: 25, page: 0 }}
                            autoHeight
                            density='compact'
                            brand={this.props.brand}
                            onBrandUpdate={this.props.setCurrentBrand}
                            processRowUpdate={async (updatedRow, originalRow) => {
                                const attrId = updatedRow.id;
                                const patch = {
                                    ...(updatedRow.value !== originalRow.value && { value: updatedRow.value }),
                                    ...(updatedRow.details !== originalRow.details && { details: updatedRow.details }),
                                };
                                const obs = attributeService.patch({ id: `${attrId}` }, patch, this.props.brand).pipe(
                                    tap((attribute) => {
                                        this.props.setCurrentBrand({
                                            ...this.props.brand,
                                            attributes: this.props.brand.attributes?.map((attr) =>
                                                attr.id === attribute.id ? attribute : attr,
                                            ),
                                        });
                                    }),
                                );

                                return firstValueFrom(obs);
                            }}
                        />
                    </TabPanel>
                    <TabPanel value={this.state.tab} index={TAB_ATTRIBUTE_HISTORY}>
                        <BrandAttributeHistory brand={this.props.brand} />
                    </TabPanel>
                    <TabPanel value={this.state.tab} index={TAB_MOVE_TAXONOMY}>
                        <BrandDetailsMoveTaxonomyTab brand={this.props.brand} />
                    </TabPanel>
                </Grid>
            </Grid>
        );
    }

    private doTabSwitch(tab: number): void {
        this.setState({ tab });
    }
}

const mapStateToProps = (state: RootState) => ({
    possibleAttributes: selectPossibleAttributes(state),
    showProd: selectProdDataStatus(state),
});

const thunks = { setCurrentBrand, prepareAttributes, saveNewAttribute };

const enhanced = connect(mapStateToProps, thunks)(BrandDetails);

export { enhanced as BrandDetails };
